/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Else, If, Then } from "react-if";
import styled from "styled-components";
import { Sidebar, Text, theme } from "@nordcloud/gnui";
import {
  CloudProviderType,
  Maybe,
  useCloudAccountsQuery,
} from "~/generated/graphql";
import { Pagination, PaginationState } from "~/components/Pagination";
import { UniversalWrap } from "~/components/Utils";
import { useQueryState } from "~/hooks";
import { isNotEmpty } from "~/tools";
import { AccountSelectorItemList } from "./AccountSelectorItemList";

const Footer = styled.div`
  flex: 1;
`;

type Props = {
  onClose: () => void;
  isOpen: boolean;
  onSubmit: (id: string, provider?: Maybe<CloudProviderType>) => void;
  title: string;
};

export function AccountSelector({ onClose, isOpen, onSubmit, title }: Props) {
  const {
    state: { limit, page },
  } = useQueryState<PaginationState>();
  const { data, loading, error } = useCloudAccountsQuery({
    variables: {
      limit,
      page,
      filter: {
        providerType: [
          CloudProviderType.Kubernetes,
          CloudProviderType.Openshift,
        ],
      },
    },
  });

  const accounts = data?.cloudAccounts?.accounts ?? [];

  return (
    <Sidebar
      isOpen={isOpen}
      title={title}
      footer={
        <Footer>
          <Pagination small count={data?.cloudAccounts?.count ?? 0} />
        </Footer>
      }
      onClick={onClose}
    >
      <UniversalWrap
        loaderProps={{
          loading,
          inContent: true,
        }}
        errorProps={{
          error,
        }}
      >
        <If condition={isNotEmpty(accounts)}>
          <Then>
            <AccountSelectorItemList items={accounts} onSubmit={onSubmit} />
          </Then>
          <Else>
            <Text
              css={{
                paddingTop: theme.spacing.spacing04,
                paddingBottom: theme.spacing.spacing04,
              }}
            >
              No accounts matching selected criteria
            </Text>
          </Else>
        </If>
      </UniversalWrap>
    </Sidebar>
  );
}
